import { useClient } from 'utils/auth-context'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useToast } from '@chakra-ui/react'
import { ErrorType } from 'types/common.types'
import { dateFormat } from 'utils/date-time-utils'
import { Vendor } from 'types/vendor.types'
import autoTable from 'jspdf-autotable'
import { currencyFormatter } from 'utils/string-formatters'
import { useParams } from 'react-router-dom'
import { PROJECT_FINANCIAL_OVERVIEW_API_KEY } from './projects'
import {
  ESTIMATE_STATUS,
  ESTIMATE_STATUSES_ASSOCIATE_WITH_CURRENT_STATUS,
  ESTIMATE_STATUSES_ASSOCIATE_WITH_CURRENT_STATUS_VENDOR,
  ESTIMATE_STATUS_OPTIONS,
} from 'features/work-order-estimates/wo-estimate-status'
import { GET_TRANSACTIONS_BY_WORK_ORDER_API_KEY } from './transactions'
import { PROPERTY_FINANCIAL_OVERVIEW_KEY } from 'features/ticket-page/tickets-inline-table'
import { useUserRolesSelector } from 'utils/redux-common-selectors'

export const useEstimateStatusOptions = (estimate: any) => {
  const status = estimate?.estimateStatus

  const { isVendor } = useUserRolesSelector()

  let selectOptions = ESTIMATE_STATUSES_ASSOCIATE_WITH_CURRENT_STATUS[status] || []

  if (isVendor) {
    selectOptions = ESTIMATE_STATUSES_ASSOCIATE_WITH_CURRENT_STATUS_VENDOR[status] || []
  }
  return { selectOptions: selectOptions }
}

export const ALL_ESTIMATES_KEY = 'all_estimates'
export const useEstimates = (workOrderId: any) => {
  const client = useClient()
  return useQuery<any>([ALL_ESTIMATES_KEY, workOrderId], async () => {
    const response = await client(`workorder/${workOrderId}/estimates`, {})
    return response?.data
  })
}

export const useCreateEstimate = () => {
  const client = useClient()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { workOrderId: ticketId } = useParams<'workOrderId'>()
  const { projectId: propertyId } = useParams<'projectId'>()

  return useMutation(
    (payload: any) => {
      return client('workorder/estimate', {
        method: 'POST',
        data: payload,
      })
    },
    {
      onError(error: ErrorType) {
        toast({
          title: error?.title || 'Error while updating estimate.',
          description: error?.message || 'Something went wrong.',
          status: 'error',
          isClosable: true,
          position: 'top-left',
        })
      },
      onSuccess: (response: any) => {
        queryClient.invalidateQueries([ALL_ESTIMATES_KEY, response?.data?.woId])
        queryClient.cancelQueries([ALL_ESTIMATES_KEY, response?.data?.woId])
        queryClient.invalidateQueries([ALL_ESTIMATES_KEY, response?.data?.woId])

        queryClient.invalidateQueries([PROJECT_FINANCIAL_OVERVIEW_API_KEY, Number(ticketId)])
        queryClient.invalidateQueries(['transactions', Number(ticketId)])
        queryClient.invalidateQueries(['transactions', ticketId?.toString()])
        queryClient.invalidateQueries(['GetProjectWorkOrders', Number(ticketId)])
        queryClient.invalidateQueries(['WorkOrderDetails', response?.data?.woId])
        queryClient.invalidateQueries(['project', Number(ticketId)])
        queryClient.invalidateQueries(['project-vendor', propertyId?.toString()])
        queryClient.invalidateQueries(['documents', Number(ticketId)])

        queryClient.invalidateQueries([GET_TRANSACTIONS_BY_WORK_ORDER_API_KEY, Number(ticketId)])
        queryClient.invalidateQueries([PROPERTY_FINANCIAL_OVERVIEW_KEY, propertyId])
        queryClient.invalidateQueries('tickets')
        queryClient.invalidateQueries('all_tickets')

        toast({
          title: 'New Estimate.',
          description: 'Estimate has been created successfully.',
          status: 'success',
          isClosable: true,
          position: 'top-left',
        })
      },
    },
  )
}

export const useUpdateEstimate = () => {
  const client = useClient()
  const toast = useToast()

  const queryClient = useQueryClient()

  const { workOrderId: ticketId } = useParams<'workOrderId'>()

  const TICKETS_QUERY_KEY = 'tickets'
  const ALL_TICKETS_QUERY_KEY = 'all_tickets'

  const { projectId: propertyId } = useParams<'projectId'>()

  return useMutation(
    (payload: any) => {
      return client('workorder/estimate', {
        method: 'PUT',
        data: payload,
      })
    },
    {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries([ALL_ESTIMATES_KEY, response?.data?.woId])
        queryClient.cancelQueries([ALL_ESTIMATES_KEY, response?.data?.woId])
        queryClient.invalidateQueries([ALL_ESTIMATES_KEY, response?.data?.woId])

        queryClient.invalidateQueries([PROJECT_FINANCIAL_OVERVIEW_API_KEY, Number(ticketId)])
        queryClient.invalidateQueries(['transactions', Number(ticketId)])
        queryClient.invalidateQueries(['transactions', ticketId?.toString()])
        queryClient.invalidateQueries(['GetProjectWorkOrders', Number(ticketId)])
        queryClient.invalidateQueries(['WorkOrderDetails', response?.data?.woId])
        queryClient.invalidateQueries(['project', Number(ticketId)])
        queryClient.invalidateQueries(['project-vendor', propertyId?.toString()])
        queryClient.invalidateQueries(['documents', Number(ticketId)])

        queryClient.invalidateQueries([GET_TRANSACTIONS_BY_WORK_ORDER_API_KEY, Number(ticketId)])
        queryClient.invalidateQueries([PROPERTY_FINANCIAL_OVERVIEW_KEY, propertyId])
        queryClient.invalidateQueries(TICKETS_QUERY_KEY)
        queryClient.invalidateQueries(ALL_TICKETS_QUERY_KEY)

        toast({
          title: 'Update Estimate.',
          description: 'Estimate has been updated successfully.',
          status: 'success',
          isClosable: true,
          position: 'top-left',
        })
      },
      onError(error: ErrorType) {
        toast({
          title: error?.title || 'Error while updating estimate.',
          description: error?.message || 'Something went wrong.',
          status: 'error',
          isClosable: true,
          position: 'top-left',
        })
      },
    },
  )
}

export const useGetVendor = (vendorId: any) => {
  const client = useClient()

  const { data, ...rest } = useQuery<Array<Vendor>>(
    ['vendor', vendorId],
    async () => {
      const response = await client(`vendors/${vendorId}`, {})

      return response?.data
    },
    {
      enabled: !!vendorId,
    },
  )

  return {
    vendorData: data,
    ...rest,
  }
}

export const estimateDefaultFormValues = (createdBy: string, property: any, vendor: any, workOrderId): any => {
  const clientRate = property?.client?.billingRate
  const vendorGeneralHourlyRate = vendor?.generalHourlyRate

  return {
    workOrderId: null,
    status: ESTIMATE_STATUS_OPTIONS.find(e => e.value === ESTIMATE_STATUS.PENDING),
    dateCreated: dateFormat(new Date()),
    createdBy,
    modifiedBy: null,
    modifiedDate: null,
    attachment: null,
    laborCost: [
      {
        itemDesc: '',
        itemCost: 0,
        itemVendorRate: 0,
        itemClientRate: 0,
        itemType: 1,
        woId: workOrderId,
        itemAmount: 0,
        vendorHourlyRate: vendorGeneralHourlyRate,
        clientHourlyRate: clientRate,
        documents: [],
      },
    ],
    materialCost: [
      {
        itemDesc: '',
        itemCost: 0,
        itemClientRate: 0, // this is profit%
        itemAmount: 0,
        itemType: 2,
        woId: workOrderId,
        documents: [],
      },
    ],
  }
}

export const parseFormValuesToPayload = (formValues: any, workOrderId) => {
  const laborItems = formValues?.laborCost
  const materialItems = formValues?.materialCost

  return {
    ...(formValues?.id ? { id: formValues?.id } : {}),
    woId: workOrderId,
    estimateStatus: formValues?.status?.value,
    submitEstimate: formValues?.submitEstimate,
    workOrderEstimateItems: laborItems?.concat(materialItems),
    flatRate: formValues?.flatRate,
  }
}

export const parsePayloadToFormValues = (estimate: any) => {
  /*if ( estimate?.workOrderEstimateItems ) {
    estimate.workOrderEstimateItems = estimate?.workOrderEstimateItems?.map( item => {
      item.documents = item.documents.map( document => {
        if ( ! document?.id ) return document;
        document.workOrderEstimateItems = item.id;
        return document;
      } );
      return item;
    } );
  }*/
  const laborCostItems = estimate?.workOrderEstimateItems.filter(i => i.itemType === 1)
  const materialCostItems = estimate?.workOrderEstimateItems.filter(i => i.itemType === 2)
  return {
    ...estimate,
    status: ESTIMATE_STATUS_OPTIONS.find(s => s.value === estimate?.estimateStatus),
    laborCost: laborCostItems,
    dateCreated: estimate?.createdDate,
    materialCost: materialCostItems,
    flatRate: estimate?.flatRate,
  }
}

export const SINGLE_ESTIMATE_KEY = 'estimate'

export const useEstimate = (estimateId: any, workOrderId: any) => {
  const client = useClient()
  return useQuery<any>(
    [SINGLE_ESTIMATE_KEY, estimateId],
    async () => {
      const response = await client(`workorder/${workOrderId}/estimates/${estimateId}`, {})
      return response?.data
    },
    {
      enabled: !!estimateId,
    },
  )
}

// cross browser test url : https://cdn.glitch.me/4c9ebeb9-8b9a-4adc-ad0a-238d9ae00bb5%2Fmdn_logo-only_color.svg
async function addImageProcess(src) {
  return new Promise((resolve, reject) => {
    let img = new Image()
    img.src = src
    img.crossOrigin = 'Anonymous'
    img.onload = () => {
      var dataURI = getBase64Image(img)
      return resolve(dataURI)
    }
    img.onerror = () => {
      return resolve(null)
    }
  })
}

function getBase64Image(img) {
  var canvas = document.createElement('canvas')

  canvas.width = img.width
  canvas.height = img.height
  var ctx = canvas.getContext('2d')
  ctx?.drawImage(img, 0, 0)

  var dataURL = canvas.toDataURL('image/jpeg')

  return dataURL.replace(/^data:image\/(png|jpg);base64,/, '')
}

export const addImages = async images => {
  const results = await Promise.all(
    images.map(async (image: any, index: number) => {
      const img = await addImageProcess(image)
      return img
    }),
  )
  return results
}

export const createEstimatePdf = async ({
  doc,
  estimate,
  property,
  workOrder,
  projectData,
  hidePrice,
  onSave,
  isVendor,
}) => {
  const laborCost = estimate?.workOrderEstimateItems?.filter(e => e.itemType === 1)
  const materialCost = estimate?.workOrderEstimateItems?.filter(e => e.itemType === 2)
  let uploadedImages = [] as any[]
  estimate?.workOrderEstimateItems?.forEach(l => {
    const itemImages = l.documents?.filter(d => !!d.s3Url).map(i => i.s3Url)
    uploadedImages = [...uploadedImages, ...itemImages]
  })

  let newImages = [] as any[]
  estimate?.workOrderEstimateItems?.forEach(l => {
    const itemImages = l.documents?.filter(d => !!d.fileObject).map(i => i.fileObject)
    newImages = [...newImages, ...itemImages]
  })

  const clientLaborCost = laborCost.reduce((a, i) => {
    return a + i.itemClientRate
  }, 0)

  const clientMaterialCost = materialCost.reduce((a, i) => {
    return a + (i.itemCost + (i.itemCost * i.itemClientRate) / 100)
  }, 0)

  const clientTotalCost = Number(clientLaborCost) + Number(clientMaterialCost)
  const vendorTotalCost = estimate?.totalVendorAmount
  const workOrderInfo = [
    { label: 'Start Date:', value: workOrder?.workOrderStartDate ?? '' },
    {
      label: 'Time:',
      value:
        workOrder?.clientSchedule?.find(
          cs => cs?.preferredStartTime?.value?.toString() === workOrder?.workOrderStartTime?.toString(),
        )?.preferredStartTime?.label ?? '',
    },
    { label: 'Priority:', value: projectData?.taskPriority ?? '' },
  ]

  const basicFont = undefined
  const summaryFont = 'Times-Roman'
  const heading = 'Estimate - WO # ' + workOrder?.id
  const startx = 15
  doc.setFontSize(16)
  doc.setFont(basicFont, 'bold')
  doc.text(heading, startx, 20)

  const unfilteredImages = await addImages(['wo-logo-tree.png', ...uploadedImages])
  // removing images which are not loaded (corrupt)
  const images = unfilteredImages.filter(i => !!i)
  doc.addImage(images[0], 'png', 160, 5, 35, 35)
  doc.setFontSize(11)
  doc.setFont(summaryFont, 'bold')
  doc.text('Property Address:', startx, 55)
  doc.setFont(summaryFont, 'normal')

  const streetAddress = property?.streetAddress || ''
  const maxLineLength = 30
  let lines = [] as any
  let currentPosition = 0

  while (currentPosition < streetAddress.length) {
    const remainingText = streetAddress.substring(currentPosition)
    const maxLength = Math.min(remainingText.length, maxLineLength)

    // Find the last space character within the maxLength
    let lastSpaceIndex = remainingText.lastIndexOf(' ', maxLength)

    // If no space found, split at maxLength
    if (lastSpaceIndex === -1) {
      lastSpaceIndex = maxLength
    }

    lines.push(remainingText.substring(0, lastSpaceIndex).trim())
    currentPosition += lastSpaceIndex + 1
  }
  lines = lines.slice(0, 4)
  lines.forEach((line, index) => {
    doc.text(line.trim(), startx, 60 + 5 * index)
  })
  const lastLineY = 65 + 5 * (lines.length - 1)

  //doc.text(property?.streetAddress ? truncateWithEllipsis(property?.streetAddress, 24) : '', startx, 60)
  doc.text(property?.city + ' ' + property?.state + ' , ' + property?.zipCode, startx, lastLineY)

  const primaryResident = property?.residents?.find(r => r.primaryResident)
  doc.setFont(summaryFont, 'bold')
  const centerTextX = 75
  doc.text('Resident:', centerTextX, 55)
  doc.setFont(summaryFont, 'normal')
  doc.text(primaryResident?.firstName + ' ' + primaryResident?.lastName ?? '', centerTextX + 18, 55)
  doc.setFont(summaryFont, 'bold')
  doc.text('Phone:', centerTextX, 60)
  doc.setFont(summaryFont, 'normal')
  doc.text(primaryResident?.phoneNumber ?? '', centerTextX + 18, 60)
  doc.setFont(summaryFont, 'bold')
  doc.text('Email:', centerTextX, 65)
  doc.setFont(summaryFont, 'normal')
  doc.text(primaryResident?.emailAddress ?? '', centerTextX + 18, 65)

  const x = 155
  let y = 50

  workOrderInfo.forEach(inv => {
    doc.setFont(summaryFont, 'bold')
    doc.text(inv.label, x + 5, y + 5)
    doc.setFont(summaryFont, 'normal')
    doc.text(
      inv.label === 'Start Date:' || inv.label === 'Expected Completion:' ? dateFormat(inv.value) || '' : inv.value,
      x + 25,
      y + 5,
    )
    y = y + 5
  })

  doc.setFont(summaryFont, 'bold')
  doc.text('Work Type:', startx, y + 25)
  doc.setFont(summaryFont, 'normal')
  doc.text(workOrder?.skillName ?? '', startx + 30, y + 25)
  doc.setFont(summaryFont, 'bold')
  doc.text('Total:', x + 5, y + 25)
  doc.setFont(summaryFont, 'normal')
  !isVendor
    ? doc.text(currencyFormatter(clientTotalCost ?? 0), x + 25, y + 25)
    : doc.text(currencyFormatter(vendorTotalCost ?? 0), x + 25, y + 25)

  doc.setFont(summaryFont, 'bold')
  doc.text('Labor Cost', startx, y + 35)
  autoTable(doc, {
    startY: y + 40,
    headStyles: { fillColor: '#FFFFFF', textColor: '#000000', lineColor: '#000000', lineWidth: 0.1 },
    theme: 'grid',
    bodyStyles: { lineColor: '#000000', minCellHeight: 15 },
    body: [
      ...laborCost.map(ai => {
        return {
          itemDesc: ai.itemDesc,
          itemCost: ai.itemCost,
          itemClientRate: currencyFormatter(ai.itemClientRate),
        }
      }),
    ],
    columnStyles: {
      location: { cellWidth: 40 },
      sku: { cellWidth: 25 },
      productName: { cellWidth: 40 },
      description: { cellWidth: 50 },
      quantity: { cellWidth: 25 },
    },
    columns: [
      { header: 'Description', dataKey: 'itemDesc' },
      { header: 'Hours', dataKey: 'itemCost' },
      ...(!isVendor ? [{ header: 'Client', dataKey: 'itemClientRate' }] : []),
    ],
  })
  const tableEndsY = (doc as any).lastAutoTable.finalY

  doc.text('Material Cost', startx, tableEndsY + 10)

  autoTable(doc, {
    startY: tableEndsY + 15,
    headStyles: { fillColor: '#FFFFFF', textColor: '#000000', lineColor: '#000000', lineWidth: 0.1 },
    theme: 'grid',
    bodyStyles: { lineColor: '#000000', minCellHeight: 15 },
    body: [
      ...materialCost.map(ai => {
        return {
          itemDesc: ai.itemDesc,
          itemCost: currencyFormatter(ai.itemCost),
          itemAmount: currencyFormatter(ai.itemAmount),
        }
      }),
    ],
    columnStyles: {
      location: { cellWidth: 40 },
      sku: { cellWidth: 25 },
      productName: { cellWidth: 40 },
      description: { cellWidth: 50 },
      quantity: { cellWidth: 25 },
    },
    columns: [
      { header: 'Description', dataKey: 'itemDesc' },
      ...(isVendor ? [{ header: 'Cost', dataKey: 'itemCost' }] : []),
      ...(!isVendor ? [{ header: 'Amount', dataKey: 'itemAmount' }] : []),
    ],
  })

  doc.setFontSize(10)
  doc.setFont(basicFont, 'normal')
  const secondTableEndsY = (doc as any).lastAutoTable.finalY
  let imgX = 15
  let imgY = secondTableEndsY + 20
  // start new page if there is less space after table ends.
  if (doc.internal.pageSize.getHeight() - imgY < 40) {
    doc.addPage()
    imgY = 5
  }
  images?.splice(0, 1)
  const imagesArray = images?.concat(newImages)
  imagesArray?.forEach((image, index) => {
    doc.addImage(image, 'png', imgX, imgY, 35, 35)
    if (imagesArray?.length - 1 === index) {
      return
    }
    if ((index + 1) % 4 === 0) {
      imgX = 15
      imgY = imgY + 50
      // start new page if there is less space after images.
      if (doc.internal.pageSize.getHeight() - imgY < 40) {
        doc.addPage()
        imgY = 5
      }
    } else {
      imgX = imgX + 50
    }
  })
  const pdfUri = doc.output('datauristring')

  doc.save(
    `${
      property?.streetAddress + '_' + property?.state + '_' + property?.city + '_' + property?.zipCode + '_estimate.pdf'
    }`.replace(/\s/g, '_'),
  )
  onSave?.([
    {
      documentType: 1101,
      estimateId: estimate?.id,
      workOrderId: workOrder?.id,
      projectId: projectData?.id,
      fileObject: pdfUri.split(',')[1],
      fileObjectContentType: 'application/pdf',
      fileType: `${
        property?.streetAddress +
        '_' +
        property?.state +
        '_' +
        property?.city +
        '_' +
        property?.zipCode +
        '_estimate.pdf'
      }`.replace(/\s/g, '_'),
    },
  ])
}
